import { useId, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { FieldContext } from './field-context';
import { Label } from './label';

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  /** Already-translated error message. */
  error?: ReactNode;
  required?: boolean;
  id?: string;
  className?: string;
  children: ReactNode;
}

export function Field({
  label,
  hint,
  error,
  required = false,
  id,
  className,
  children,
}: FieldProps) {
  const autoId = useId();
  const controlId = id ?? `field-${autoId}`;
  const hintId = hint ? `${controlId}-hint` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return (
    <FieldContext.Provider
      value={{ id: controlId, describedBy, invalid: Boolean(error), required }}
    >
      <div className={cn('flex flex-col gap-2', className)} data-invalid={error ? '' : undefined}>
        {label ? (
          <Label htmlFor={controlId}>
            {label}
            {required ? (
              <span aria-hidden className="ms-1 text-primary">
                *
              </span>
            ) : null}
          </Label>
        ) : null}
        {children}
        {error ? (
          <p id={errorId} role="alert" className="text-xs text-danger">
            {error}
          </p>
        ) : hint ? (
          <p id={hintId} className="text-xs text-muted">
            {hint}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}
